import { useState, useRef, useContext } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, ArrowLeft, Image as ImageIcon, Upload, X, 
  Loader2, Star, Plus, Check
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API, AuthContext } from "../../App";

const OnboardingPortfolio = () => {
  const navigate = useNavigate();
  const { onboardingData, refreshData } = useOutletContext();
  const { refreshUser } = useContext(AuthContext);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [featuredIds, setFeaturedIds] = useState(new Set());

  // Check if portfolio album exists
  const portfolioAlbumId = onboardingData?.photographer?.portfolio_album_id;
  const portfolioCount = onboardingData?.stats?.portfolio_photo_count || 0;

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate files
    const validFiles = files.filter(file => {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      // First, ensure portfolio album exists
      let albumId = portfolioAlbumId;
      
      if (!albumId) {
        // Create portfolio album
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const albumResponse = await axios.post(`${API}/galleries`, {
          title: "Portfolio",
          description: "My portfolio showcase",
          is_private: false,
          is_portfolio: true
        }, { headers });
        
        albumId = albumResponse.data.gallery_id;
        
        // Set as portfolio album
        await axios.put(`${API}/photographer/portfolio-album`, {
          gallery_id: albumId
        }, { headers });
      }

      // Upload photos
      const token = localStorage.getItem("token");
      const uploadedPhotos = [];
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(
          `${API}/galleries/${albumId}/upload`,
          formData,
          {
            headers: { 
              "Content-Type": "multipart/form-data",
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
          }
        );

        uploadedPhotos.push({
          id: response.data.asset_id,
          filename: response.data.filename,
          url: `${API}/media/${albumId}/${response.data.filename}`
        });

        setUploadProgress(Math.round(((i + 1) / validFiles.length) * 100));
      }

      setPhotos(prev => [...prev, ...uploadedPhotos]);
      toast.success(`${validFiles.length} photo(s) uploaded!`);
      await refreshData();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload photos");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const toggleFeatured = (photoId) => {
    setFeaturedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(photoId)) {
        newSet.delete(photoId);
      } else if (newSet.size < 5) {
        newSet.add(photoId);
      } else {
        toast.info("You can select up to 5 featured photos");
      }
      return newSet;
    });
  };

  const handleContinue = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(`${API}/onboarding/step/portfolio`, {}, { headers });
      toast.success("Portfolio step completed!");
      await refreshData();
      if (refreshUser) await refreshUser();
      navigate("/dashboard/onboarding/payments");
    } catch (error) {
      toast.error("Failed to continue");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate("/dashboard/onboarding/profile");
  };

  return (
    <div data-testid="onboarding-step-3">
      <div className="mb-8">
        <h1 className="text-3xl font-serif mb-2">Portfolio Starter</h1>
        <p className="text-muted-foreground">
          Upload some of your best work to showcase on your profile. You can skip this and add photos later.
        </p>
      </div>

      <div className="space-y-6">
        {/* Upload Area */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Upload Photos
            </CardTitle>
            <CardDescription>
              Add photos that represent your best work. These will appear on your public profile.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />

            {/* Drop Zone */}
            <div
              onClick={() => !uploading && fileInputRef.current?.click()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
                transition-colors
                ${uploading ? "border-primary bg-primary/5" : "border-border hover:border-primary hover:bg-muted/50"}
              `}
            >
              {uploading ? (
                <div className="space-y-4">
                  <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
                  <p className="text-muted-foreground">Uploading... {uploadProgress}%</p>
                  <div className="w-48 h-2 bg-muted rounded-full mx-auto overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-lg mb-2">Drop photos here or click to browse</p>
                  <p className="text-sm text-muted-foreground">
                    PNG, JPG, or WebP • Max 10MB per photo
                  </p>
                </>
              )}
            </div>

            {/* Portfolio Stats */}
            {(portfolioCount > 0 || photos.length > 0) && (
              <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Portfolio photos: <span className="font-medium text-foreground">{portfolioCount + photos.length}</span>
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate("/dashboard/portfolio")}
                  >
                    Manage Portfolio
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recently Uploaded Photos */}
        {photos.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5" />
                Just Uploaded
              </CardTitle>
              <CardDescription>
                Click photos to mark them as featured (shown first on your profile)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {photos.map((photo) => (
                  <motion.div
                    key={photo.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`
                      relative aspect-square rounded-lg overflow-hidden cursor-pointer
                      ring-2 transition-all
                      ${featuredIds.has(photo.id) ? "ring-primary" : "ring-transparent hover:ring-muted"}
                    `}
                    onClick={() => toggleFeatured(photo.id)}
                  >
                    <img
                      src={photo.url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    {featuredIds.has(photo.id) && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-primary-foreground" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tip Card */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <p className="text-sm">
              <strong>💡 Tip:</strong> Profiles with portfolio photos get 3x more booking requests. 
              You can always add more photos from your dashboard later.
            </p>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleContinue} disabled={saving} data-testid="continue-btn">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                {photos.length === 0 ? "Skip for Now" : "Continue"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPortfolio;
